import React, { useState, useEffect, useRef } from 'react'
import { FlaskConical, Play, X, Maximize, Minimize } from 'lucide-react'
import GeoGebraSimulation from '../../../components/interactive/shared/simulations/GeoGebraSimulation'
import InteractiveContentRenderer from '../../../components/interactive/InteractiveContentRenderer'
import { GeoGebraService } from '../../../services/video/GeoGebraService'
import { SimulationLibrary } from '../../../services/simulation/SimulationLibrary'
import PyodideRunner from '../components/PyodideRunner'
import { genId } from '../lib/id'
import type { SimulationCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

const PROJECTILE_EXAMPLE = `import numpy as np
import matplotlib.pyplot as plt

g = 9.8        # gia tốc trọng trường (m/s^2)
v0 = 20        # vận tốc đầu (m/s)
angle = 45     # góc ném (độ)

a = np.radians(angle)
t_flight = 2 * v0 * np.sin(a) / g
t = np.linspace(0, t_flight, 200)
x = v0 * np.cos(a) * t
y = v0 * np.sin(a) * t - 0.5 * g * t**2

plt.figure(figsize=(6, 4))
plt.plot(x, y)
plt.title('Quỹ đạo chuyển động ném xiên')
plt.xlabel('x (m)'); plt.ylabel('y (m)')
plt.grid(True)

print('Tầm xa:', round(x[-1], 2), 'm')
print('Độ cao cực đại:', round(y.max(), 2), 'm')
`

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as SimulationCard
  const ggbList = GeoGebraService.getAll()
  const sims = SimulationLibrary.getAll().filter((s) => s.kind === 'canvas')

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Loại mô phỏng</label>
        <select
          className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
          value={c.kind}
          onChange={(e) => onChange({ kind: e.target.value as SimulationCard['kind'] })}
        >
          <option value="geogebra">GeoGebra</option>
          <option value="library">Kho mô phỏng JavaScript</option>
          <option value="python">Python (Pyodide)</option>
        </select>
      </div>

      {c.kind === 'geogebra' && (
        <>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase">Chọn mô phỏng GeoGebra có sẵn</label>
            <select
              className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
              value={c.ggbFile || ''}
              onChange={(e) => onChange({ ggbFile: e.target.value || undefined, materialId: undefined })}
            >
              <option value="">— Chọn từ thư viện —</option>
              {ggbList.map((g) => (
                <option key={g.id} value={g.filename}>
                  {g.title} (L{g.lop}·C{g.chuong}·B{g.bai})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase">hoặc GeoGebra material id</label>
            <input
              className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
              value={c.materialId || ''}
              onChange={(e) => onChange({ materialId: e.target.value || undefined, ggbFile: undefined })}
              placeholder="vd: abcd1234 (nếu không dùng file .ggb)"
            />
          </div>
        </>
      )}

      {c.kind === 'library' && (
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase">Chọn mô phỏng từ kho (JavaScript)</label>
          <select
            className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
            value={c.simType || ''}
            onChange={(e) => {
              const s = sims.find((x) => x.type === e.target.value)
              onChange({ simType: e.target.value || undefined, simParams: s?.params, title: c.title || s?.title } as Partial<SimulationCard>)
            }}
          >
            <option value="">— Chọn mô phỏng —</option>
            {sims.map((s) => (
              <option key={s.id} value={s.type}>
                {s.title} (L{s.lop}·C{s.chuong}·B{s.bai})
              </option>
            ))}
          </select>
          <p className="text-[11px] text-gray-400 mt-1">Mô phỏng canvas tương tác đã có trong kho (không cần AI).</p>
        </div>
      )}

      {c.kind === 'python' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-gray-500 uppercase">Mã Python (matplotlib/numpy)</label>
            <button type="button" onClick={() => onChange({ code: PROJECTILE_EXAMPLE })} className="text-xs font-bold text-violet-600 hover:text-violet-700">
              Chèn ví dụ ném xiên
            </button>
          </div>
          <textarea
            className="w-full min-h-[200px] p-3 border border-gray-200 rounded-lg text-xs font-mono outline-none focus:ring-2 focus:ring-blue-200"
            value={c.code || ''}
            onChange={(e) => onChange({ code: e.target.value })}
            placeholder="Dùng matplotlib.pyplot để vẽ — kết quả hiện ở phần xem trước khi bấm Chạy."
          />
        </div>
      )}
    </div>
  )
}

// Render mô phỏng thực tế (trong modal Xem mô phỏng). availH = chiều cao khả dụng (px).
const SimRunner: React.FC<{ c: SimulationCard; availH: number }> = ({ c, availH }) => {
  if (c.kind === 'python') {
    return c.code ? <PyodideRunner code={c.code} /> : <p className="text-gray-400 text-sm">Chưa có mã Python.</p>
  }
  if (c.kind === 'library') {
    if (!c.simType) return <p className="text-gray-400 text-sm">Chưa chọn mô phỏng.</p>
    // Bọc trong khung KHỚP độ phân giải: rộng tối đa container, cao tối đa availH, tự co cho vừa (không tràn).
    // Nếu là mô hình 3D thì cho full chiều rộng (max-w-none)
    const is3D = c.simType.endsWith('_3d')
    return (
      <div className={`mx-auto w-full ${is3D ? 'max-w-none' : 'max-w-[1100px]'}`} style={{ height: availH }}>
        <InteractiveContentRenderer content={[{ type: c.simType, id: c.id, height: availH, ...(c.simParams || {}) }] as any} />
      </div>
    )
  }
  // geogebra — lấp đầy chiều cao khả dụng
  if (c.ggbFile) {
    return <GeoGebraSimulation filename={GeoGebraService.getPublicPath(c.ggbFile)} title={c.title || 'Mô phỏng GeoGebra'} height={availH} showCustomControls />
  }
  if (c.materialId) {
    return <iframe title={c.title || 'Mô phỏng GeoGebra'} src={`https://www.geogebra.org/material/iframe/id/${c.materialId}`} className="w-full rounded-lg border border-gray-200 bg-white" style={{ height: availH }} allowFullScreen />
  }
  return <p className="text-gray-400 text-sm">Chưa cấu hình mô phỏng.</p>
}

const HEADER_H = 56  // chiều cao thanh tiêu đề modal (px)

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as SimulationCard
  const [open, setOpen] = useState(false)
  const [fs, setFs] = useState(false)
  const [vh, setVh] = useState(typeof window !== 'undefined' ? window.innerHeight : 800)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const upd = () => setVh(window.innerHeight)
    const onFs = () => setFs(!!document.fullscreenElement)
    upd()
    window.addEventListener('resize', upd)
    document.addEventListener('fullscreenchange', onFs)
    return () => {
      window.removeEventListener('resize', upd)
      document.removeEventListener('fullscreenchange', onFs)
    }
  }, [open])

  const toggleFs = () => {
    if (!document.fullscreenElement) boxRef.current?.requestFullscreen?.().catch(() => {})
    else document.exitFullscreen?.().catch(() => {})
  }

  const close = () => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {})
    setOpen(false)
  }

  // Chiều cao khả dụng cho mô phỏng = chiều cao khung (toàn màn hình hay 92% viewport) trừ thanh tiêu đề.
  const availH = Math.max(360, (fs ? vh : Math.round(vh * 0.92)) - HEADER_H)

  const configured = (c.kind === 'geogebra' && (c.ggbFile || c.materialId)) || (c.kind === 'library' && c.simType) || (c.kind === 'python' && c.code)
  const label = c.kind === 'geogebra' ? 'GeoGebra' : c.kind === 'library' ? 'Mô phỏng tương tác' : 'Python'

  if (!configured) {
    return <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">Chưa cấu hình mô phỏng.</div>
  }

  return (
    <>
      <div className="flex items-center gap-3 p-4 rounded-xl border border-violet-200 bg-violet-50/60">
        <span className="w-11 h-11 rounded-xl bg-violet-500 text-white flex items-center justify-center shrink-0">
          <FlaskConical size={22} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[11px] font-black uppercase tracking-wide text-violet-600">{label}</span>
          <span className="block text-base font-bold text-gray-800 truncate">{c.title || 'Mô phỏng Vật lí'}</span>
        </span>
        <button
          onClick={() => setOpen(true)}
          className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700"
        >
          <Play size={16} /> Xem mô phỏng
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-0 md:p-4" onClick={close}>
          <div
            ref={boxRef}
            className={`bg-white shadow-2xl w-full flex flex-col overflow-hidden ${fs ? 'h-screen max-w-none rounded-none' : 'max-w-6xl rounded-2xl'}`}
            style={fs ? undefined : { height: availH + HEADER_H }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 border-b shrink-0" style={{ height: HEADER_H }}>
              <h3 className="font-black text-gray-800 truncate">{c.title || 'Mô phỏng'}</h3>
              <div className="flex items-center gap-1">
                <button onClick={toggleFs} title={fs ? 'Thoát toàn màn hình' : 'Toàn màn hình'} className="p-2 text-gray-400 hover:bg-gray-100 rounded-xl">
                  {fs ? <Minimize size={18} /> : <Maximize size={18} />}
                </button>
                <button onClick={close} title="Đóng" className="p-2 text-gray-400 hover:bg-gray-100 rounded-xl"><X size={20} /></button>
              </div>
            </div>
            <div className="flex-1 min-h-0 min-w-0 overflow-auto p-3 md:p-4 bg-slate-50">
              <SimRunner c={c} availH={availH} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export const simulationCardDef: CardDef = {
  type: 'simulation',
  label: 'Mô phỏng',
  description: 'GeoGebra / Kho mô phỏng JS',
  icon: FlaskConical,
  color: 'bg-violet-50 text-violet-600',
  createDefault: (): SimulationCard => ({ id: genId(), type: 'simulation', kind: 'geogebra', height: 520 }),
  Editor,
  Preview,
}
